import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { referralAPI, settingsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Share2, DollarSign, TrendingUp, Users, ArrowRight, Check, Loader2, Mail, Phone } from 'lucide-react';

export default function BecomeReferralAgentPage() {
  const navigate = useNavigate();
  const [referralEnabled, setReferralEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });

  useEffect(() => {
    // Check if referral program is enabled
    const checkStatus = async () => {
      try {
        const res = await referralAPI.getStatus();
        setReferralEnabled(res.data?.enabled !== false);
      } catch (error) {
        console.error('Error checking referral status:', error);
      } finally {
        setLoading(false);
      }
    };
    checkStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.password) {
      toast.error('Please fill all required fields');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSubmitting(true);
    try {
      await referralAPI.apply(formData);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginData.email || !loginData.password) {
      toast.error('Please enter email and password');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await referralAPI.login(loginData);
      localStorage.setItem('referral_agent_token', res.data.token);
      localStorage.setItem('referral_agent', JSON.stringify(res.data.agent));
      toast.success('Login successful!');
      navigate('/referral-agent/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!referralEnabled) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md p-8 text-center">
          <Share2 className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-2xl font-bold mb-2">Referral Program</h1>
          <p className="text-muted-foreground">
            Our referral program is not currently accepting new applications. Please check back later.
          </p>
          <Button asChild className="mt-6">
            <Link to="/">Return Home</Link>
          </Button>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Application Submitted!</h1>
          <p className="text-muted-foreground mb-6">
            Thank you for applying to become a referral agent. We'll review your application and notify you via email once approved.
          </p>
          <Button asChild>
            <Link to="/">Return Home</Link>
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-primary/80 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Share2 className="h-16 w-16 mx-auto mb-4" />
          <h1 className="text-4xl font-bold mb-4">Become a Referral Agent</h1>
          <p className="text-xl opacity-90">
            Earn commissions by promoting our tours. Share your unique referral links and get paid for every booking.
          </p>
        </div>
      </div>

      {/* How It Works */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold text-center mb-8">How It Works</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-primary">1</span>
            </div>
            <h3 className="font-semibold mb-2">Sign Up</h3>
            <p className="text-sm text-muted-foreground">
              Create your free account and get access to your personalized referral dashboard.
            </p>
          </Card>
          
          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-primary">2</span>
            </div>
            <h3 className="font-semibold mb-2">Share Your Links</h3>
            <p className="text-sm text-muted-foreground">
              Get unique referral links for each tour and share them with your audience.
            </p>
          </Card>
          
          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-primary">3</span>
            </div>
            <h3 className="font-semibold mb-2">Earn Commission</h3>
            <p className="text-sm text-muted-foreground">
              Earn a commission for every completed booking made through your referral link.
            </p>
          </Card>
        </div>

        {/* Benefits */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <div className="flex items-start gap-3">
            <DollarSign className="h-6 w-6 text-green-500 flex-shrink-0" />
            <div>
              <h4 className="font-semibold">Competitive Commissions</h4>
              <p className="text-sm text-muted-foreground">Earn up to 15% commission on every booking</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <TrendingUp className="h-6 w-6 text-blue-500 flex-shrink-0" />
            <div>
              <h4 className="font-semibold">Real-Time Tracking</h4>
              <p className="text-sm text-muted-foreground">Track your clicks, conversions, and earnings</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Users className="h-6 w-6 text-purple-500 flex-shrink-0" />
            <div>
              <h4 className="font-semibold">Marketing Support</h4>
              <p className="text-sm text-muted-foreground">Access to promotional materials and deals</p>
            </div>
          </div>
        </div>
      </div>

      {/* Application Form / Login */}
      <div className="max-w-md mx-auto px-4 pb-16">
        <Card className="p-6">
          <div className="flex gap-4 mb-6">
            <Button
              variant={!showLogin ? 'default' : 'outline'}
              className="flex-1"
              onClick={() => setShowLogin(false)}
            >
              Apply Now
            </Button>
            <Button
              variant={showLogin ? 'default' : 'outline'}
              className="flex-1"
              onClick={() => setShowLogin(true)}
            >
              Agent Login
            </Button>
          </div>

          {!showLogin ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  required
                />
              </div>
              
              <div>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  required
                />
              </div>
              
              <div>
                <Label>Phone Number *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 787 555 1234"
                  required
                />
              </div>
              
              <div>
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>
              
              <div>
                <Label>Confirm Password *</Label>
                <Input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                  required
                />
              </div>
              
              <Button type="submit" className="w-full gap-2" disabled={submitting}>
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Submit Application
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
              
              <p className="text-xs text-muted-foreground text-center">
                By applying, you agree to our terms and conditions for referral agents.
              </p>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  placeholder="john@example.com"
                  required
                />
              </div>
              
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  placeholder="••••••••"
                  required
                />
              </div>
              
              <Button type="submit" className="w-full gap-2" disabled={submitting}>
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Login to Dashboard
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
